/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-02 17:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Page, Layout } from '@shopify/polaris';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import MainMenu from 'src/components/containers/main-menu';
import DidYouKnow from 'src/components/views/sync/did-you-know.overview';
import ShopifyCurrentLimitations from './shopify-current-limitations';
import { URL } from 'src/constants/links';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';

import { startSABLoading, stopSABLoading } from 'src/integrations/shopify-app-bridge/loading';
import { buildSyncTypeOptions } from 'src/helpers/sync-helper';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { SYNC_TYPE_FLOW_MANUAL_SYNC } from 'src/constants/sync';

import * as syncActions from 'actions/sync-actions';
import TitleHelp from 'src/components/views/common/title-help';
import SyncStatus from 'src/components/views/sync/v2/SyncStatus';
import ManualSync from 'src/components/views/sync/v2/ManualSync';
import SyncHistory from 'src/components/views/sync/v2/SyncHistory';
import SyncSchedule from 'src/components/views/sync/v2/SyncSchedule';
import { displayStoreDate } from 'src/helpers/date-time-helper';

type Props = {
  multipleCurrencies: boolean,
  getAvailableSyncListLoading: boolean,
  getAvailableSyncListSuccess: boolean,
  availableSyncList: Array<string>,
  getAvailableSyncList: () => void,
  resetSyncStatus: () => void
};

type State = {
  syncTypeOptions: Array<{ value: string, label: string }>,
  currentTime: Date,
  showProgressBar: boolean
};

type ComposedProps = Props & WithI18nProps;

class SyncOverview extends Component<ComposedProps, State> {
  state = {
    syncTypeOptions: [],
    currentTime: new Date(),
    isHideSettingsTab: false,
    isOpenScheduleSync: false,
    showProgressBar: false,
    valueSelect: null
  };

  componentDidMount() {
    const {
      getAvailableSyncList,
      getSyncSchedule,
      planName,
      isPlanExpired,
      hasSyncSettingsDisplay,
      i18n
    } = this.props;
    const syncTypeOptions = buildSyncTypeOptions([SYNC_TYPE_FLOW_MANUAL_SYNC], i18n);
    this.setState({ syncTypeOptions });

    getAvailableSyncList();
    // get first sync schedule
    getSyncSchedule();

    // Check condition is store expired or plan name in list below => hide settings tab
    if (
      isPlanExpired ||
      ['frozen', 'cancelled', 'closed', 'fraudulent', 'paused'].includes(planName) ||
      !hasSyncSettingsDisplay
    ) {
      this.setState({ isHideSettingsTab: true });
    }
  }

  componentWillUnmount() {
    const { resetSyncStatus } = this.props;
    resetSyncStatus();
  }

  componentWillReceiveProps(nextProps: ComposedProps) {
    const {
      getAvailableSyncListLoading: getAvailableSyncListLoadingNext,
      availableSyncList,
      i18n
    } = nextProps;

    const { getAvailableSyncListLoading } = this.props;
    // Get available sync list
    if (getAvailableSyncListLoading && !getAvailableSyncListLoadingNext) {
      const syncTypeOptions = buildSyncTypeOptions(availableSyncList, i18n);
      this.setState({ syncTypeOptions });
    }

    if (getAvailableSyncListLoadingNext && !getAvailableSyncListLoading) {
      startSABLoading();
    }

    if (!getAvailableSyncListLoadingNext && getAvailableSyncListLoading) {
      stopSABLoading();
    }
  }

  onOpenScheduleSync = (status, valueSelect) => {
    this.setState({ isOpenScheduleSync: status, valueSelect });
  };

  renderNextSyncScheduleDate = scheduleAt => {
    if (!scheduleAt) return '';
    if (new Date(scheduleAt * 1000).getTime() < new Date().getTime()) return '';
    return displayStoreDate(scheduleAt, 'hh:mm A, MMM D');
  };

  render() {
    const { syncTypeOptions, isHideSettingsTab, isOpenScheduleSync, showProgressBar, valueSelect } =
      this.state;
    let { multipleCurrencies, i18n, triggerSyncLoading } = this.props;

    const tabs = [
      {
        id: 'overview',
        content: i18n.translate('AdminHTML.Sync.Sync.tab1_Content'),
        panelID: 'overview-tab'
      },
      {
        id: 'sync-schedule',
        content: i18n.translate('AdminHTML.Sync.Sync.tab2_Content'),
        panelID: 'sync-schedule-tab'
      },
      {
        id: 'sync-history',
        content: i18n.translate('AdminHTML.Sync.Sync.tab3_Content'),
        panelID: 'sync-history-tab'
      }
    ];
    if (!isHideSettingsTab) {
      tabs.push({
        id: 'settings',
        content: i18n.translate('AdminHTML.Sync.Sync.tab4_Content'),
        panelID: 'settings-tab'
      });
    }

    return (
      <div className="BC-Page BC-Page__Sync">
        <MainMenu menuId="menu-sync" />
        {isOpenScheduleSync ? (
          <Page
            title={i18n.translate('AdminHTML.Sync.Sync.pageTitle_Schedule')}
            breadcrumbs={[
              {
                content: '',
                onAction: () => {
                  this.onOpenScheduleSync(false, valueSelect);
                }
              }
            ]}>
            <div className="sync-v2-wrapper">
              <Layout>
                <Layout.Section>
                  <div className="BC-Container__Notifications">
                    {/* Did you know? */}
                    <DidYouKnow />
                  </div>
                </Layout.Section>
                <Layout.Section>
                  <div style={{ width: '100%' }}>
                    <SyncSchedule
                      syncTypeOptions={syncTypeOptions}
                      currentTime={this.state.currentTime}
                      valueSelect={valueSelect}
                      onSelectValueSelect={valueSelect => {
                        this.setState({ valueSelect });
                      }}
                    />
                  </div>
                </Layout.Section>
              </Layout>
            </div>
          </Page>
        ) : (
          <Page title={i18n.translate('AdminHTML.Sync.Sync.pageTitle_Overview')}>
            <TitleHelp i18n={i18n} hrefUserManual={URL['Link_V2_Sync_ManualGuide']} />
            <div className="sync-v2-wrapper">
              <Layout>
                <Layout.Section>
                  <div className="BC-Container__Notifications">
                    {/* Installation reminder status banner */}
                    <InstallationRemindStatusBanner />
                    {/* Did you know? */}
                    <DidYouKnow />
                    {/* Sticky notification for exceeded plan */}
                    <ExceededPlanNotification />
                  </div>
                </Layout.Section>

                {/* Shopify current limitations */}
                {multipleCurrencies && <ShopifyCurrentLimitations />}

                <Layout.Section>
                  <div className="sync-v2-wrapper-flex">
                    <div className="sync-status">
                      <SyncStatus
                        onOpenScheduleSync={this.onOpenScheduleSync}
                        showProgressBar={showProgressBar}
                        renderNextSyncScheduleDate={this.renderNextSyncScheduleDate}
                      />
                    </div>
                    <div className="manual-sync">
                      <ManualSync
                        valueSelect={valueSelect}
                        syncTypeOptions={syncTypeOptions}
                        triggerSyncLoading={triggerSyncLoading}
                        onOpenScheduleSync={this.onOpenScheduleSync}
                        onShowProgress={() => this.setState({ showProgressBar: true })}
                        renderNextSyncScheduleDate={this.renderNextSyncScheduleDate}
                      />
                    </div>
                  </div>
                </Layout.Section>
                <Layout.Section>
                  <SyncHistory></SyncHistory>
                </Layout.Section>
              </Layout>
            </div>
          </Page>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const planName = _.get(state.oauth, 'tenantApp.externalInfo.plan_name', {});
  const hasSyncSettingsDisplay = _.get(
    state.oauth,
    'tenantApp.settings.syncSettingsDisplay.settings.toggleAutoSync',
    false
  );
  const isPlanExpired =
    _.get(state.oauth, 'tenantApp.trialExpired', false) ||
    _.get(state.oauth, 'tenantApp.exceededPlan', false) ||
    _.get(state.oauth, 'tenantApp.validateRegularPlanOffer.expiredAt', false);

  return {
    multipleCurrencies: _.get(state.oauth.tenantApp, 'features.multipleCurrencies', false),
    getAvailableSyncListLoading: _.get(state.sync, 'getAvailableSyncListLoading', false),
    getAvailableSyncListSuccess: _.get(state.sync, 'getAvailableSyncListSuccess', false),
    syncSchedule: _.get(state.sync, 'syncSchedule', {}),
    flow: _.get(state.sync, 'flow', {}),
    availableSyncList: _.get(state.sync, 'availableSyncList', []),
    appLanguage: _.get(state.oauth, 'tenantApp.appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
    planName,
    isPlanExpired,
    hasSyncSettingsDisplay,
    triggerSyncLoading: _.get(state.sync, 'triggerSyncLoading', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get available sync list
  getAvailableSyncList: () => dispatch(syncActions.getAvailableSyncList(ownProps.i18n)),
  // Get sync schedule
  getSyncSchedule: () => dispatch(syncActions.getSyncSchedule(ownProps.i18n)),
  resetSyncStatus: () => dispatch(syncActions.resetSyncStatus())
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SyncOverview));
